/**
 * Pure projection helpers for Luci conversation control.
 *
 * The platform is the versioned source of truth for control, so nothing here
 * derives a mode. These helpers only decide how a mode the platform already
 * reported should be presented, and what moving the switch would do next.
 */

export const CONTROL_MODES = ['ai', 'human', 'paused', 'technical_handoff'];

export const isKnownMode = mode => CONTROL_MODES.includes(mode);

/**
 * The switch has two positions. Only `human` means a person is driving;
 * every other mode sits on the Luci side.
 */
export const isHumanControlled = mode => mode === 'human';

/**
 * `paused` and `technical_handoff` are neither Luci answering nor a person
 * handling it, so a two position switch alone would misrepresent them. The
 * caller shows the real state alongside rather than quietly rounding it.
 */
export const hasAmbiguousPosition = mode =>
  isKnownMode(mode) && mode !== 'ai' && mode !== 'human';

/**
 * What moving the switch would do. Null when the operator may not change
 * control or the mode is unknown, so the caller renders nothing rather than
 * offering an action it cannot perform.
 */
export const pendingTransition = ({ mode, canChangeControl }) => {
  if (!canChangeControl || !isKnownMode(mode)) return null;
  return isHumanControlled(mode) ? 'activate_luci' : 'take_control';
};

export const modeTranslationKey = mode =>
  isKnownMode(mode)
    ? `CONVERSATION.LUCI_CONTROL.MODE.${mode.toUpperCase()}`
    : '';

const MODE_CLASSES = {
  ai: 'text-n-teal-11',
  human: 'text-n-amber-11',
  paused: 'text-n-slate-11',
  technical_handoff: 'text-n-ruby-11',
};

export const modeClass = mode => MODE_CLASSES[mode] ?? 'text-n-slate-11';
